import {
  parseIsoDate,
  safeEndTime,
  startOfLocalDay,
  toLocalDayKey,
} from "./dates.js";
import type {
  AppUsage,
  Client,
  ClientReportRow,
  DailyReportRow,
  Project,
  TimeEntry,
} from "./models.js";

interface DayDurationSegment {
  dayKey: string;
  durationSeconds: number;
}

/**
 * Filter entries whose local-day range overlaps [fromDay, toDay].
 * An overnight entry that starts before toDay and ends after fromDay is included.
 * Pass null for an open-ended bound.
 */
export function filterEntriesByDateRange(
  entries: TimeEntry[],
  fromDay: string | null,
  toDay: string | null,
): TimeEntry[] {
  if (!fromDay && !toDay) return entries;

  return entries.filter((entry) => {
    const entryStartDay = toLocalDayKey(entry.startTime);
    const entryEndDay = toLocalDayKey(entry.endTime);
    if (toDay && entryStartDay > toDay) return false;
    if (fromDay && entryEndDay < fromDay) return false;
    return true;
  });
}

export function buildDailyReportRows(entries: TimeEntry[], appUsages: AppUsage[]): DailyReportRow[] {
  const usagesByEntry = new Map<string, AppUsage[]>();
  const countedDurationByDay = new Map<string, number>();
  const sessionsByDay = new Map<string, Set<string>>();

  for (const usage of appUsages) {
    const existing = usagesByEntry.get(usage.timeEntryId);
    if (existing) {
      existing.push(usage);
    } else {
      usagesByEntry.set(usage.timeEntryId, [usage]);
    }
  }

  for (const entry of entries) {
    for (const segment of splitEntryAcrossDays(entry.startTime, entry.endTime)) {
      const daySessions = sessionsByDay.get(segment.dayKey) ?? new Set<string>();
      daySessions.add(entry.id);
      sessionsByDay.set(segment.dayKey, daySessions);
    }

    const countedUsages = (usagesByEntry.get(entry.id) ?? []).filter(
      (usage) => usage.countsTowardTrackedTime,
    );

    if (countedUsages.length === 0) {
      distributeDuration(
        entry.startTime,
        entry.endTime,
        entry.countedDurationSeconds,
        countedDurationByDay,
      );
      continue;
    }

    for (const usage of countedUsages) {
      distributeDuration(
        usage.startTime,
        usage.endTime,
        durationSecondsBetween(usage.startTime, usage.endTime),
        countedDurationByDay,
      );
    }
  }

  return [...sessionsByDay.keys()]
    .sort((left, right) => (left < right ? 1 : -1))
    .map((date) => ({
      date,
      sessionCount: sessionsByDay.get(date)?.size ?? 0,
      countedDurationSeconds: countedDurationByDay.get(date) ?? 0,
    }));
}

export function buildClientReportRows(
  clients: Client[],
  projects: Project[],
  entries: TimeEntry[],
): ClientReportRow[] {
  const clientsById = new Map(clients.map((client) => [client.id, client]));
  const projectsById = new Map(projects.map((project) => [project.id, project]));
  const grouped = new Map<string | null, TimeEntry[]>();

  for (const entry of entries) {
    const clientId = projectsById.get(entry.projectId)?.clientId ?? null;
    const existing = grouped.get(clientId);
    if (existing) {
      existing.push(entry);
    } else {
      grouped.set(clientId, [entry]);
    }
  }

  return [...grouped.entries()]
    .map(([clientId, clientEntries]) => {
      const clientName = clientId ? clientsById.get(clientId)?.name ?? "Unknown Client" : "Unknown Client";
      const projectCount = new Set(
        clientEntries.map((entry) => projectsById.get(entry.projectId)?.id).filter(Boolean),
      ).size;

      return {
        clientId,
        clientName,
        projectCount,
        countedDurationSeconds: clientEntries.reduce(
          (total, entry) => total + entry.countedDurationSeconds,
          0,
        ),
      };
    })
    .sort((left, right) => {
      if (left.countedDurationSeconds === right.countedDurationSeconds) {
        return left.clientName.localeCompare(right.clientName);
      }

      return right.countedDurationSeconds - left.countedDurationSeconds;
    });
}

function distributeDuration(
  startTime: string,
  endTime: string,
  totalDurationSeconds: number,
  totals: Map<string, number>,
): void {
  if (totalDurationSeconds <= 0) {
    return;
  }

  const segments = splitEntryAcrossDays(startTime, endTime);
  const rawDuration = segments.reduce((total, segment) => total + segment.durationSeconds, 0);

  if (rawDuration <= 0) {
    return;
  }

  let allocated = 0;

  segments.forEach((segment, index) => {
    const weighted =
      index === segments.length - 1
        ? totalDurationSeconds - allocated
        : Math.round(totalDurationSeconds * (segment.durationSeconds / rawDuration));

    allocated += weighted;
    totals.set(segment.dayKey, (totals.get(segment.dayKey) ?? 0) + weighted);
  });
}

function splitEntryAcrossDays(startTime: string, endTime: string): DayDurationSegment[] {
  const start = parseIsoDate(startTime);
  const end = safeEndTime(start, parseIsoDate(endTime));
  const segments: DayDurationSegment[] = [];
  let cursor = start;

  while (cursor < end) {
    const dayStart = startOfLocalDay(cursor);
    const nextDay = new Date(dayStart);
    nextDay.setDate(nextDay.getDate() + 1);
    const segmentEnd = end < nextDay ? end : nextDay;
    const durationSeconds = Math.max(
      0,
      Math.round((segmentEnd.getTime() - cursor.getTime()) / 1000),
    );

    if (durationSeconds > 0) {
      segments.push({ dayKey: toLocalDayKey(dayStart), durationSeconds });
    }

    cursor = segmentEnd;
  }

  if (segments.length === 0) {
    segments.push({ dayKey: toLocalDayKey(startOfLocalDay(start)), durationSeconds: 0 });
  }

  return segments;
}

function durationSecondsBetween(startTime: string, endTime: string): number {
  const start = parseIsoDate(startTime);
  const end = safeEndTime(start, parseIsoDate(endTime));
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
}
